import { useEffect, useRef, useState } from 'react';
import { rouletteColor } from '@raketta/shared';
import { useAppStore } from '../store/useAppStore';
import { getRouletteSocket } from '../lib/socket';
import { telegram } from '../lib/telegram';
import { formatTon } from '../lib/format';

const BET_WINDOW = 25;
const CHIPS = [1, 5, 25, 100];

const GRID_ROWS: number[][] = [];
for (let n = 1; n <= 36; n += 3) GRID_ROWS.push([n, n + 1, n + 2]);

type Phase = 'connecting' | 'betting' | 'spinning' | 'cooldown';
type Recent = { pocket: number; color: string };
type BetMap = Record<string, number>;

const colorLabel = (c: string) => (c === 'red' ? 'Красное' : c === 'black' ? 'Чёрное' : 'Зеро');

export function RouletteScreen() {
  const setBalance = useAppStore((s) => s.setBalance);
  const showToast = useAppStore((s) => s.showToast);
  const openVerify = useAppStore((s) => s.openVerify);

  const [phase, setPhase] = useState<Phase>('connecting');
  const [roundId, setRoundId] = useState(0);
  const [betEndsAt, setBetEndsAt] = useState(0);
  const [recent, setRecent] = useState<Recent[]>([]);
  const [displayPocket, setDisplayPocket] = useState<number | null>(null);
  const [result, setResult] = useState<Recent | null>(null);
  const [lastBetId, setLastBetId] = useState<number | null>(null);
  const [bets, setBets] = useState<BetMap>({});
  const [chip, setChip] = useState(5);
  const [now, setNow] = useState(Date.now());

  const offsetRef = useRef(0);
  const animRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // тикаем локальные часы для обратного отсчёта
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const s = getRouletteSocket();

    const applyState = (d: any) => {
      if (d?.now) offsetRef.current = d.now - Date.now();
      setPhase(d.phase);
      setRoundId(d.roundId);
      setBetEndsAt(d.betEndsAt);
      if (d.recent) setRecent(d.recent);
      if (d.pocket != null && d.phase !== 'betting') {
        setDisplayPocket(d.pocket);
        setResult({ pocket: d.pocket, color: rouletteColor(d.pocket) });
      }
    };

    const onBetting = (d: any) => {
      if (d?.now) offsetRef.current = d.now - Date.now();
      if (animRef.current) clearInterval(animRef.current);
      setPhase('betting');
      setRoundId(d.roundId);
      setBetEndsAt(d.betEndsAt);
      setRecent(d.recent || []);
      setBets({});
      setLastBetId(null);
    };

    const onSpinning = () => {
      setPhase('spinning');
      if (animRef.current) clearInterval(animRef.current);
      animRef.current = setInterval(() => setDisplayPocket(Math.floor(Math.random() * 37)), 60);
    };

    const onResult = (d: any) => {
      if (animRef.current) clearInterval(animRef.current);
      setPhase('cooldown');
      setDisplayPocket(d.pocket);
      setResult({ pocket: d.pocket, color: d.color });
      setRecent(d.recent || []);
    };

    const onSettled = (d: any) => {
      setBalance(d.newBalance);
      setLastBetId(d.betId);
      const net = parseFloat(d.totalPayout) - parseFloat(d.totalStaked);
      telegram.notify(net > 0 ? 'success' : 'error');
      if (net > 0) showToast(`Выигрыш +${formatTon(net)}`);
    };

    const onAuthErr = () => showToast('Сокет: ошибка авторизации', true);

    s.on('round:state', applyState);
    s.on('round:betting', onBetting);
    s.on('round:spinning', onSpinning);
    s.on('round:result', onResult);
    s.on('roulette:settled', onSettled);
    s.on('auth:error', onAuthErr);
    s.emit('roulette:sync', (snap: any) => snap && applyState(snap));

    return () => {
      s.off('round:state', applyState);
      s.off('round:betting', onBetting);
      s.off('round:spinning', onSpinning);
      s.off('round:result', onResult);
      s.off('roulette:settled', onSettled);
      s.off('auth:error', onAuthErr);
      if (animRef.current) clearInterval(animRef.current);
    };
  }, []);

  const msLeft = Math.max(0, betEndsAt - (now + offsetRef.current));
  const secondsLeft = Math.ceil(msLeft / 1000);
  const canBet = phase === 'betting' && msLeft > 800;
  const totalStaked = Object.values(bets).reduce((s, v) => s + v, 0);
  const pocketColor = displayPocket != null ? rouletteColor(displayPocket) : 'green';

  const place = (spot: string, type: string, number?: number) => {
    if (!canBet) {
      showToast('Ставки закрыты', true);
      return;
    }
    const s = getRouletteSocket();
    telegram.haptic('light');
    s.emit('roulette:bet', { type, number, amount: String(chip) }, (ack: any) => {
      if (ack?.ok) {
        setBalance(ack.newBalance);
        setBets((b) => ({ ...b, [spot]: (b[spot] || 0) + chip }));
      } else {
        showToast(ack?.error || 'Ставка не принята', true);
      }
    });
  };

  const Cell = ({ n }: { n: number }) => {
    const color = rouletteColor(n);
    const amt = bets[`straight:${n}`];
    const isResult = result?.pocket === n && phase !== 'betting';
    return (
      <button className={`rl-num ${color} ${isResult ? 'hit' : ''}`} onClick={() => place(`straight:${n}`, 'straight', n)}>
        {n}
        {amt ? <span className="rl-chip-badge">{amt}</span> : null}
      </button>
    );
  };

  const Outside = ({ spot, label }: { spot: string; label: string }) => {
    const amt = bets[spot];
    return (
      <button className="rl-out" onClick={() => place(spot, spot)}>
        {label}
        {amt ? <span className="rl-chip-badge">{amt}</span> : null}
      </button>
    );
  };

  return (
    <>
      {/* Последние результаты (общие) */}
      {recent.length > 0 && (
        <div className="recent">
          {recent.map((r, i) => (
            <span key={i} className={`pill ${r.color} static`}>
              {r.pocket}
            </span>
          ))}
        </div>
      )}

      {/* Результат + таймер раунда */}
      <div className="card rl-result-card">
        <div className={`rl-result ${pocketColor} ${phase === 'spinning' ? 'spinning' : ''}`}>
          {displayPocket != null ? displayPocket : '—'}
        </div>
        <div className="rl-result-meta">
          {phase === 'betting' ? (
            <>
              <div className="rl-color-label">Ставки открыты</div>
              <div className="muted">до спина {secondsLeft} с</div>
              <div className="rl-progress">
                <div className="rl-progress-fill" style={{ width: `${(Math.min(secondsLeft, BET_WINDOW) / BET_WINDOW) * 100}%` }} />
              </div>
            </>
          ) : phase === 'spinning' ? (
            <div className="rl-color-label">Крутится…</div>
          ) : result ? (
            <>
              <div className="rl-color-label">
                {colorLabel(result.color)} · {result.pocket}
              </div>
              {lastBetId ? (
                <button className="rl-verify" onClick={() => openVerify(lastBetId)}>
                  Проверить честность
                </button>
              ) : (
                <div className="muted">Следующий раунд скоро…</div>
              )}
            </>
          ) : (
            <div className="muted">Подключение…</div>
          )}
        </div>
      </div>

      {/* Стол */}
      <div className={`card rl-board ${canBet ? '' : 'locked'}`}>
        <div className="rl-zero-row">
          <button className={`rl-num green zero ${result?.pocket === 0 && phase !== 'betting' ? 'hit' : ''}`} onClick={() => place('straight:0', 'straight', 0)}>
            0
            {bets['straight:0'] ? <span className="rl-chip-badge">{bets['straight:0']}</span> : null}
          </button>
        </div>
        <div className="rl-grid">
          {GRID_ROWS.map((row) => (
            <div className="rl-grid-row" key={row[0]}>
              {row.map((n) => (
                <Cell key={n} n={n} />
              ))}
            </div>
          ))}
        </div>
        <div className="rl-outside">
          <div className="rl-row">
            <Outside spot="dozen1" label="1-12" />
            <Outside spot="dozen2" label="13-24" />
            <Outside spot="dozen3" label="25-36" />
          </div>
          <div className="rl-row">
            <Outside spot="low" label="1-18" />
            <Outside spot="even" label="Чёт" />
            <Outside spot="red" label="Красное" />
            <Outside spot="black" label="Чёрное" />
            <Outside spot="odd" label="Нечёт" />
            <Outside spot="high" label="19-36" />
          </div>
        </div>
      </div>

      {/* Фишки */}
      <div className="card stack">
        <div className="rl-chips">
          {CHIPS.map((c) => (
            <button key={c} className={`rl-chip ${chip === c ? 'active' : ''}`} onClick={() => setChip(c)}>
              {c}
            </button>
          ))}
        </div>
        <div className="row-between">
          <span className="muted">
            В этом раунде: <b className="tnum" style={{ color: 'var(--text)' }}>{formatTon(totalStaked)}</b>
          </span>
          <span className="muted">раунд #{roundId}</span>
        </div>
      </div>
    </>
  );
}
